import { faEnvelope, faPhone } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import { useEffect, useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { useUserParams } from "../hooks/useUserParams";

export default function Contact() {
  const [popup, setPopup] = useState(false);
  const {
    getCurrentAgentName,
    getCurrentAgentTitle,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentBrokerage,
  } = useUserParams();
  const [formData, setFormData] = useState({
    Name: "",
    "Email Address": "",
    Message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  async function submitForm(event) {
    event.preventDefault();

    const data = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Contact Form Message",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(data),
      });

      const response = await res.json();

      if (response?.success) {
        console.log("Success", response);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          Name: "",
          "Email Address": "",
          Message: "",
        });
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
  }

  const seoConfig = pageSEO.contact();
  return (
    <>
      <SEO {...seoConfig} />

      <section className="relative bg-[#F8FAFC] pt-36 pb-24 overflow-hidden">
        <div className="absolute top-0 left-0 h-[500px] w-[500px] rounded-full bg-harbour-accent/10 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full bg-harbour-blue/10 blur-3xl" />

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="relative z-10 max-w-7xl mx-auto px-6"
        >
          <div className="mb-16">
            <motion.div variants={fadeUp} className="flex items-center gap-4 mb-6">
              <div className="h-px w-14 bg-harbour-accent" />
              <p className="uppercase tracking-[0.4em] font-medium text-harbour-blue">
                Start A Conversation
              </p>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="font-serif text-5xl lg:text-6xl leading-none text-harbour-navy max-w-3xl"
            >
              Contact Us
            </motion.h1>

            <motion.p
              variants={fadeUp}
              className="mt-8 max-w-2xl text-lg leading-8 text-harbour-muted"
            >
              Whether you are searching for your next home, selling a property, or
              exploring your options, our team provides trusted guidance throughout
              every step of your real estate journey.
            </motion.p>
          </div>

          <motion.div
            variants={fadeUp}
            className="grid gap-10 lg:grid-cols-[1.6fr_1fr]"
          >
            <div className="rounded-[40px] bg-white p-8 lg:p-12 shadow-[0_30px_80px_rgba(15,23,42,0.08)] border border-harbour-border">

              <div className="mb-10">
                <h2 className="font-serif text-3xl text-harbour-navy">
                  Send A Message
                </h2>
                <p className="mt-3 text-sm text-harbour-muted">
                  We will get back to you as soon as possible.
                </p>
              </div>

              <form onSubmit={submitForm} className="space-y-6">

                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.3em] text-harbour-blue">
                    Full Name
                  </label>
                  <input
                    name="Name"
                    value={formData.Name}
                    onChange={handleInputChange}
                    placeholder="John Smith"
                    className="w-full rounded-2xl border border-harbour-border bg-[#F8FAFC] px-5 py-4 text-harbour-navy outline-none transition focus:border-harbour-accent focus:bg-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.3em] text-harbour-blue">
                    Email Address
                  </label>
                  <input
                    name="Email Address"
                    value={formData["Email Address"]}
                    onChange={handleInputChange}
                    placeholder="john@email.com"
                    className="w-full rounded-2xl border border-harbour-border bg-[#F8FAFC] px-5 py-4 text-harbour-navy outline-none transition focus:border-harbour-accent focus:bg-white"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-xs uppercase tracking-[0.3em] text-harbour-blue">
                    Message
                  </label>
                  <textarea
                    name="Message"
                    value={formData.Message}
                    onChange={handleInputChange}
                    placeholder="How can we help?"
                    rows={5}
                    className="w-full rounded-2xl border border-harbour-border bg-[#F8FAFC] px-5 py-4 text-harbour-navy outline-none transition focus:border-harbour-accent focus:bg-white"
                  />
                </div>

                <button
                  type="submit"
                  className="btn-harbour rounded-full px-10"
                >
                  Send Message
                </button>

              </form>
            </div>

            <div className="relative overflow-hidden rounded-[40px] bg-harbour-navy p-10 lg:p-12 text-white">

              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-harbour-accent/20 blur-3xl" />

              <div className="relative">

                <span className="text-xs uppercase tracking-[0.4em] text-harbour-accent">
                  Contact Details
                </span>

                <h2 className="mt-6 font-serif text-4xl leading-tight">
                  {getCurrentAgentName()}
                </h2>

                <p className="mt-4 text-white/60 leading-7">
                  {getCurrentAgentTitle()}
                  <br />
                  {getCurrentBrokerage()}
                </p>

                <div className="mt-12 space-y-8">

                  <div className="flex items-center gap-5">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
                      <FontAwesomeIcon icon={faPhone} className="text-harbour-accent" />
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] text-white/50">
                        Phone
                      </p>
                      <p className="mt-1 text-lg">
                        {getCurrentAgentNumber()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-5">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
                      <FontAwesomeIcon icon={faEnvelope} className="text-harbour-accent" />
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-[0.3em] text-white/50">
                        Email
                      </p>
                      <p className="mt-1 text-lg break-all">
                        {getCurrentAgentEmail()}
                      </p>
                    </div>
                  </div>

                </div>

                <div className="mt-14 border-t border-white/10 pt-8">
                  <p className="text-sm leading-7 text-white/60">
                    Dedicated to providing a refined real estate experience built on
                    trust, expertise, and lasting relationships.
                  </p>
                </div>

              </div>

            </div>

          </motion.div>
        </motion.div>
      </section>

      {popup && (
        <>
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />

          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-3xl shadow-2xl p-10 max-w-md w-[90%] text-center">
            <div className="w-16 h-16 rounded-full bg-summit-highlight/20 flex items-center justify-center mx-auto mb-6">
              ✓
            </div>

            <h3 className="font-heading text-3xl text-summit-primary">
              {popup.subject}
            </h3>

            <p className="text-summit-muted mt-4 leading-7">
              {popup.message}
            </p>

            <button
              onClick={() => setPopup(false)}
              className="mt-8 bg-summit-secondary hover:bg-summit-primary transition text-white px-8 py-3 rounded-xl"
            >
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
